import React, { useCallback } from "react";
import PropTypes from "prop-types";
import {
  DialogSurface,
  DialogTitle,
  Button,
  DialogActions,
  DialogBody,
  makeStyles,
  mergeClasses
} from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";

const useStyles = makeStyles({
  title: {
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  success: {
    background: "var(--colorPaletteGreenForeground, #27AE60)",
    "&:hover, &:active, &:focus, &:hover:focus, &:active:focus, &:focus:active": {
      background: "var(--colorPaletteGreenForeground2, #27AE60)!important"
    }
  },
  error: {
    background: "#EB5757",
    "&:hover, &:active, &:focus, &:hover:focus, &:active:focus, &:focus:active": {
      background: "#EB5757!important"
    }
  },
  danger: {
    background: "#EB5757",
    "&:hover, &:active, &:focus, &:hover:focus, &:active:focus, &:focus:active": {
      background: "#EB5757!important"
    }
  },
  warning: {
    background: "var(--colorPaletteDarkOrangeForeground3, #F2994A)",
    "&:hover, &:active, &:focus, &:hover:focus, &:active:focus, &:focus:active": {
      background: "var(--colorPaletteDarkOrangeForeground3, #F2994A)!important"
    }
  },
  buttonStyle: {
    fontFamily: "var(--fontFamilyBase)",
    fontSize: "var(--fontSizeBase300)",
    fontWeight: 600,
    lineHeight: "var(--lineHeightBase300)",
    whiteSpace: "nowrap"
  }
});

const headerStyle = {
  fontFamily: "var(--fontFamilyBase)!important",
  fontSize: "20px",
  fontStyle: "normal",
  fontWeight: 600
};

export default function Dialog({
  tag,
  title,
  onCancel,
  cancelLabel,
  onConfirm,
  confirmLabel,
  confirmHref,
  bottomNav,
  children,
  maxWidth,
  classNameTitle,
  classNameContent,
  className,
  onClose,
  appearance = "primary",
  confirmDisabled,
  ...rest
}) {
  const styles = useStyles();
  const normalizedAppearance = appearance === "danger" ? "danger" : appearance;

  const onSubmitForm = useCallback(
    e => {
      e.preventDefault();

      if (onConfirm) {
        onConfirm(e);
      }
    },
    [onConfirm]
  );

  const dialogStyle = {
    background: "var(--colorNeutralBackground1)",
    maxWidth: maxWidth || "500px"
  };

  const footerStyle = {
    "@media (minWidth: 1200px)": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      alignSelf: "stretch",
      marginLeft: "auto"
    }
  };

  return (
    <DialogSurface className={className} style={dialogStyle} as={tag} onSubmit={onSubmitForm} {...rest}>
      <DialogBody>
        <DialogTitle
          className={classNameTitle}
          style={headerStyle}
          action={
            (onClose || onCancel) && (
              <Button
                className={styles.closeIcon}
                appearance="transparent"
                icon={<Dismiss20Regular />}
                onClick={onClose || onCancel}
              />
            )
          }
        >
          {title ? <div className={styles.title}>{title}</div> : null}
        </DialogTitle>
      </DialogBody>
      {children}
      {(onConfirm || onCancel || bottomNav || confirmHref) && (
        <DialogBody style={{ width: "100%" }}>
          <DialogActions style={footerStyle}>
            {bottomNav}

            {onCancel && (
              <Button className={styles.buttonStyle} appearance="secondary" onClick={onCancel}>
                {cancelLabel}
              </Button>
            )}

            {onConfirm && !confirmHref && confirmLabel && (
              <Button
                className={mergeClasses(styles.buttonStyle, styles[normalizedAppearance])}
                appearance="primary"
                type="submit"
                onClick={tag === "form" ? null : onConfirm}
                disabled={confirmDisabled}
              >
                {confirmLabel}
              </Button>
            )}
            {confirmHref && confirmLabel && (
              <Button
                className={mergeClasses(styles.buttonStyle, styles[normalizedAppearance])}
                appearance="primary"
                href={confirmHref}
                target="_blank"
                as="a"
                role="button"
                tabIndex={0}
              >
                {confirmLabel}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      )}
    </DialogSurface>
  );
}

Dialog.propTypes = {
  tag: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onCancel: PropTypes.func,
  cancelLabel: PropTypes.string,
  onConfirm: PropTypes.func,
  confirmHref: PropTypes.string,
  confirmLabel: PropTypes.string,
  bottomNav: PropTypes.node,
  children: PropTypes.node,
  maxWidth: PropTypes.string,
  classNameTitle: PropTypes.string,
  classNameContent: PropTypes.string,
  className: PropTypes.string,
  onClose: PropTypes.func,
  appearance: PropTypes.oneOf(["primary", "success", "error", "warning", "danger"]),
  confirmDisabled: PropTypes.bool
};

Dialog.defaultProps = {
  tag: "form",
  cancelLabel: "Cancel",
  appearance: "primary"
};
